import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getHubspotField } from "../api/AppLinkApi";
import RuleFieldMapper from "./component/RuleFieldMapper";
import { getRule } from "../api/RuleApi";
import { getApiBaseUrl } from "../api/api";
import InputInline from "../components/InputInline";

const HubspotSetting = ({ projectId, appInfo, onChange, connectionStatus }) => {
  const { t } = useTranslation();
  const webhookBase = getApiBaseUrl() == "" ? "https://salecrm.ngrok.io" : getApiBaseUrl();
  const { accessToken = {} } = appInfo;
  const [hubspotFields, setHubspotFields] = useState([]);
  const [mappings, setMappings] = useState({});
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (accessToken.accessToken) {
      getHubspotField("contact")
        .then((data) => {
          const { results = [] } = data;
          const fields = results
            .filter((field) => field.formField)
            .map((field) => ({ value: field.name, label: field.label }));
          setHubspotFields(fields);
          setErrorMessage("");
        })
        .catch(() => {
          setErrorMessage(t("AccessToken is invalid"));
        });
    } else {
      setErrorMessage(t("AccessToken & ClientSecret are required!"));
    }
  }, [appInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ruleId = `${projectId}-${appInfo.app}`;
    getRule(ruleId)
      .then((data) => {
        const { actions = [] } = data;
        if (actions.length > 0) {
          setMappings(actions[0].data.mappings);
          setErrorMessage("");
        }
      })
      .catch(() => {
        setErrorMessage(t("Something went wrong!. Please re-enable Hubspot app."));
      });
  }, [hubspotFields]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderError = () => {
    if (errorMessage != "") {
      return <div className={"text-red-600"}>{t(errorMessage)}</div>;
    }
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-4 mb-4">
        <div className="col-span-12 lg:col-span-3">
          <div className={"space-y-2 align-middle"}>
            <div className={"w-40"}>{t("Status")}</div>
            <div className={"text-sm text-gray-400"}>{t("Connection to HubSpot")}</div>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-9">
          <div className="flex items-center gap-x-3">
            {connectionStatus()}
            {renderError()}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4 mb-4">
        <div className="col-span-12 lg:col-span-3">
          <div className={"space-y-2 align-middle"}>
            <div className={"w-40"}>{t("Access Token")}</div>
            <div className={"text-sm text-gray-400"}>{t("Used to make API calls")}</div>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-9">
          <InputInline
            value={accessToken.accessToken}
            border={true}
            onFinish={(value) => {
              onChange({
                app: appInfo.app,
                fieldId: "accessToken",
                fieldValue: value,
              });
            }}
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4 mb-4">
        <div className="col-span-12 lg:col-span-3">
          <div className={"space-y-2 align-middle"}>
            <div className={"w-40"}>{t("Client Secret")}</div>
            <div className={"text-sm text-gray-400"}>{t("Used for signature validation")}</div>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-9">
          <InputInline
            value={accessToken.clientSecret}
            border={true}
            onFinish={(value) => {
              onChange({
                app: appInfo.app,
                fieldId: "clientSecret",
                fieldValue: value,
              });
            }}
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4 mb-4">
        <div className="col-span-12 lg:col-span-3">
          <div className={"space-y-2 align-middle"}>
            <div className={"w-40"}>{t("Target URL")}</div>
            <div className={"text-sm text-gray-400"}>{t("Webhook")}</div>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-9">
          <InputInline
            value={`${webhookBase}/hubspot/hook/${projectId}-${appInfo.app}`}
            readOnly={true}
            border={true}
          />
        </div>
      </div>
      <div className="border-t border-gray-300"></div>
      <div className={"font-bold"}>{t("Mappings")}</div>
      <RuleFieldMapper
        showContactMapping={true}
        showCompanyMapping={true}
        showDealMapping={true}
        sourceOptions={{
          SALE: hubspotFields,
          CONTACT: hubspotFields,
          COMPANY: hubspotFields,
        }}
        fieldMappings={mappings}
        onChangeMappings={(newMappings) => {
          onChange({
            app: appInfo.app,
            fieldId: "mappings",
            fieldValue: newMappings,
          });
        }}
      />
    </>
  );
};

export default HubspotSetting;
